import { useState } from "react";
import { Link, useParams } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import PageMeta from "../components/PageMeta";
import { ArrowLeft, ArrowRight, ChevronDown, Check, Shield, Lock } from "lucide-react";
import { SpeciesSelect } from "@/components/SpeciesSelect";

const PLANS = {
  steadfast: {
    name: "Steadfast",
    whimsy: "The Steadfast",
    tagline: "For the conscientious furchild parent",
    price: "S$680",
    monthly: "S$57",
    annual: 680,
    tag: null,
    gradientFrom: "hsl(338,20%,98%)",
    gradientTo: "hsl(338,36%,95%)",
    titleColor: "hsl(338, 38%, 60%)",
    features: [
      "Annual wellness examination",
      "Devoted vaccinations",
      "Genomics baseline screening",
      "Same-week appointment guarantee",
      "Personalised health record, accessible anytime",
    ],
    genomics: null,
  },
  devoted: {
    name: "Devoted",
    whimsy: "The Devoted",
    tagline: "For those who leave nothing to chance",
    price: "S$1,180",
    monthly: "S$98",
    annual: 1180,
    tag: "Most Popular",
    gradientFrom: "hsl(338,22%,97%)",
    gradientTo: "hsl(338,40%,93%)",
    titleColor: "hsl(338, 52%, 42%)",
    features: [
      "Bi-annual examinations",
      "Comprehensive bloodwork panel",
      "Advanced Genomics Panel — included",
      "One urgent care consultation per year",
      "Dental health screening",
      "Priority 24hr booking",
    ],
    genomics: null,
  },
  besotted: {
    name: "Besotted",
    whimsy: "The Besotted",
    tagline: "For parents who know no bounds",
    price: "S$2,980",
    monthly: "S$248",
    annual: 2980,
    tag: "All-inclusive",
    gradientFrom: "hsl(338,24%,97%)",
    gradientTo: "hsl(338,44%,91%)",
    titleColor: "hsl(248, 40%, 22%)",
    features: [
      "Weekly consultations",
      "Full diagnostic suite access",
      "Full Genomics Workup — included",
      "24/7 veterinary care line",
      "Annual dental procedure",
      "Daily wellness checks and specialist access",
    ],
    genomics: "Full Genomics Workup — included",
  },
};

const FAQS = [
  {
    q: "When does my membership begin?",
    a: "Your membership activates immediately upon payment confirmation. You will receive a welcome email with your digital membership card and portal access within 15 minutes.",
  },
  {
    q: "Can I change my plan after joining?",
    a: "Yes. You can upgrade at any time — the difference is prorated to your renewal date. Downgrades take effect at the next annual renewal.",
  },
  {
    q: "Is there a minimum commitment period?",
    a: "Membership is billed annually. You may cancel before your renewal date for a prorated refund, minus a S$50 administrative fee. Cancellations within 30 days of joining receive a full refund if no services have been consumed.",
  },
  {
    q: "Can I add more pets later?",
    a: "Yes. Additional pets can be added at any time. Multi-pet households receive a 20% discount on the second pet, 25% on the third, and 30% from the fourth onwards.",
  },
  {
    q: "How does the loyalty discount work?",
    a: "From Year 2 onwards, your loyalty credit grows annually — reaching up to 80% off select services by Year 12. The credit is applied automatically at each renewal.",
  },
  {
    q: "What if my pet has a pre-existing condition?",
    a: "Pre-existing conditions diagnosed before enrolment are not covered. Our veterinary team will conduct an intake assessment at your first visit to document baseline health status.",
  },
  {
    q: "Is this the same as pet insurance?",
    a: "No — and that's the point. Pet insurance requires claims, co-insurance, waiting periods, and excludes pre-existing conditions. Furbaby membership covers illness and accident treatment at our own hospital with no claims process, no excess, and no age limits. Your pet is seen, treated, and looked after — you walk out without paperwork.",
  },
];

const TC = [
  {
    title: "1. Membership and Eligibility",
    body: "Membership is open to Singapore residents aged 18 and above. Each membership covers one eligible pet (dog, cat, or small animal). Furbaby Vet Care reserves the right to decline membership applications at its discretion.",
  },
  {
    title: "2. Fees and Billing",
    body: "Membership fees are billed annually in Singapore Dollars (SGD). Fees are non-transferable and non-refundable except as set out in the cancellation policy. Furbaby reserves the right to adjust annual fees once per membership year, subject to 90 days' written notice prior to each renewal date. Any adjustment is indexed to the Singapore Vet Care Index (SVCI), which tracks veterinary labour, consumable, and facility cost inflation in Singapore, and is subject to the following caps: (a) Standard members: annual fee increases are capped at 5% per year; (b) Founding Members (first 1,000 enrolments): annual fee increases are capped at 3% per year. See Clause 8 for Founding Member terms. Furbaby will not apply any fee adjustment in the first 24 months following a member's enrolment date.",
  },
  {
    title: "3. Included Services",
    body: "Services included in each tier are defined at enrolment and may be updated with reasonable notice. Services must be consumed within the membership year and do not carry over. Unused services are forfeited upon expiry.",
  },
  {
    title: "4. Cancellation and Refunds",
    body: "Cancellations within 30 days of the start date are eligible for a full refund if no services have been consumed. After 30 days, a prorated refund of the remaining unused period applies, minus a S$50 administrative fee. No refunds are issued in the final 60 days of a membership year.",
  },
  {
    title: "5. Exclusions",
    body: "The membership does not cover: (a) pre-existing conditions; (b) elective cosmetic procedures; (c) grooming and non-clinical services; (d) specialist procedures outside the scope of Furbaby's in-house veterinary team unless explicitly included in your tier; (e) emergency hospitalisation beyond the included urgent care allocation.",
  },
  {
    title: "6. Data and Privacy",
    body: "By joining, you consent to Furbaby Vet Care collecting, processing, and storing your personal data and your pet's health records in accordance with the Singapore Personal Data Protection Act (PDPA) 2012. Health data is accessible only to Furbaby's treating veterinary team and is never shared with or sold to third parties.",
  },
  {
    title: "7. Governing Law",
    body: "These terms are governed by the laws of the Republic of Singapore. Any disputes shall be subject to the exclusive jurisdiction of the Singapore courts.",
  },
  {
    title: "8. Founding Member Rate",
    body: "The first 1,000 members to enrol with Furbaby Vet Care are designated Founding Members. Founding Members receive a preferential annual fee adjustment cap of 3% per year (in place of the standard 5% cap set out in Clause 2), guaranteed for the duration of an uninterrupted membership. Founding Member status is confirmed at enrolment, is permanent so long as the membership remains active and uninterrupted, and is non-transferable. Founding Member status lapses permanently upon cancellation; a member who re-enrols after cancelling is not entitled to reinstatement of Founding Member status. Furbaby will publish the remaining Founding Member allocation on its website; once 1,000 enrolments are confirmed, the Founding Member rate will no longer be available to new applicants.",
  },
];

function Accordion({ items, type }: { items: { q?: string; title?: string; a?: string; body?: string }[]; type: "faq" | "tc" }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="flex flex-col divide-y divide-foreground/10">
      {items.map((item, i) => {
        const heading = type === "faq" ? item.q! : item.title!;
        const content = type === "faq" ? item.a! : item.body!;
        const isOpen = open === i;
        return (
          <div key={i}>
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              className="w-full flex items-center justify-between py-5 text-left gap-4 hover:opacity-70 transition-opacity duration-300"
            >
              <span className="font-sans text-sm leading-snug opacity-75">{heading}</span>
              <ChevronDown
                className={`w-4 h-4 flex-shrink-0 opacity-40 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <p className="font-sans text-sm leading-relaxed opacity-55 pb-5">{content}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

export default function Join() {
  const params = useParams<{ tier: string }>();
  const tierKey = (params.tier?.toLowerCase() ?? "devoted") as keyof typeof PLANS;
  const plan = PLANS[tierKey] ?? PLANS.devoted;

  const [submitted, setSubmitted] = useState(false);
  const [agreeTC, setAgreeTC] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    petName: "", petType: "", petAge: "",
    cardName: "", cardNumber: "", cardExpiry: "", cardCvc: "",
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreeTC) return;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-lg"
        >
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-10">
            <Check className="w-8 h-8 text-primary-foreground" />
          </div>
          <h2 className="font-serif text-6xl md:text-7xl tracking-tighter mb-6">
            Welcome,<br /><span className="italic text-primary">{plan.whimsy.replace("The ", "")}.</span>
          </h2>
          <p className="font-sans text-lg opacity-55 leading-relaxed mb-4">
            Your membership is confirmed. A welcome email is on its way to <strong className="opacity-90">{form.email}</strong> with your digital card and portal access.
          </p>
          <p className="font-grotesk text-xs uppercase tracking-widest opacity-35 mb-12">
            {plan.name} plan · {plan.price}/year · renews annually
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link
              href="/membership"
              className="group inline-flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 border-b border-foreground/30 pb-1.5"
            >
              <ArrowLeft className="w-3 h-3" /> Membership
            </Link>
            <Link
              href="/book"
              className="group inline-flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 border-b border-foreground pb-1.5"
            >
              Book your first visit <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-400" />
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-primary/30">
      <PageMeta
        title="Join Furbaby — Choose Your Membership"
        description="Sign up for a Furbaby membership plan. Unlimited consultations, preventive care, and loyalty discounts that grow every year."
        canonical="https://furbaby.vet/main/join"
        ogTitle="Join Furbaby"
        ogDescription="Choose your Furbaby membership plan and start your furchild's wellness journey."
      />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-6 flex justify-between items-center bg-background/80 backdrop-blur-sm border-b border-foreground/6">
        <Link
          href="/membership"
          className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500"
        >
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform duration-400" />
          Membership
        </Link>
        <span className="font-serif text-2xl font-bold italic tracking-tighter">Furbaby</span>
        <div className="flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-widest opacity-35">
          <Lock className="w-3 h-3" />
          Secure
        </div>
      </nav>

      {/* Hero */}
      <section
        className="pt-36 pb-16 px-6 md:px-20"
        style={{ background: `linear-gradient(135deg, ${plan.gradientFrom} 0%, ${plan.gradientTo} 100%)` }}
      >
        <div className="max-w-5xl mx-auto">
          {plan.tag && (
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block font-grotesk text-[9px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/40 mb-6"
            >
              {plan.tag}
            </motion.span>
          )}
          <div className="overflow-hidden mb-2">
            <motion.p
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="font-grotesk text-[10px] uppercase tracking-widest opacity-50"
            >
              {plan.name} Plan
            </motion.p>
          </div>
          <div className="overflow-hidden mb-4">
            <motion.h1
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1.2, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-[14vw] md:text-[9vw] leading-[0.85] tracking-tighter"
              style={{ color: plan.titleColor }}
            >
              {plan.whimsy}
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.55 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="font-sans text-sm md:text-base leading-relaxed max-w-sm"
          >
            {plan.tagline}
          </motion.p>
        </div>
      </section>

      {/* Main: two-col on desktop */}
      <div className="max-w-5xl mx-auto px-6 md:px-20 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">

          {/* LEFT: plan details + FAQ + T&C */}
          <div className="lg:col-span-7 flex flex-col gap-16">

            {/* What's included */}
            <section>
              <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-40 mb-8">What's included</p>
              <ul className="flex flex-col gap-5">
                {plan.features.map(f => (
                  <li key={f} className="flex items-start gap-4">
                    <span
                      className="block w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                      style={{ backgroundColor: plan.titleColor }}
                    />
                    <span className="font-sans text-base opacity-70 leading-snug">{f}</span>
                  </li>
                ))}
              </ul>
              {plan.genomics && (
                <div className="mt-8 pt-6 border-t border-foreground/10">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="w-3.5 h-3.5 opacity-50" />
                    <span className="font-grotesk text-[9px] uppercase tracking-widest opacity-60">{plan.genomics}</span>
                  </div>
                  <p className="font-sans text-xs opacity-40 ml-5">DNA profiling, personalised longevity roadmap, dedicated genomics consult</p>
                </div>
              )}
            </section>

            {/* FAQ */}
            <section>
              <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-40 mb-6">Frequently asked</p>
              <Accordion items={FAQS} type="faq" />
            </section>

            {/* T&C */}
            <section>
              <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-40 mb-6">Membership terms</p>
              <Accordion items={TC} type="tc" />
            </section>

          </div>

          {/* RIGHT: sticky payment form */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <form onSubmit={handleSubmit} className="flex flex-col gap-10">

                {/* Price summary */}
                <div
                  className="rounded-3xl p-8"
                  style={{ background: `linear-gradient(135deg, ${plan.gradientFrom} 0%, ${plan.gradientTo} 100%)` }}
                >
                  <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-50 mb-1">{plan.name} — Annual</p>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-serif text-4xl" style={{ color: plan.titleColor }}>{plan.price}</span>
                    <span className="font-grotesk text-xs opacity-40 uppercase tracking-widest">/ year</span>
                  </div>
                  <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-35">equiv. {plan.monthly}/mo</p>
                  <div className="mt-6 pt-5 border-t border-foreground/15 flex flex-col gap-2">
                    {[
                      "No setup fees",
                      "Cancel within 30 days, full refund",
                      "Loyalty discount from Year 2",
                    ].map(b => (
                      <div key={b} className="flex items-center gap-2.5">
                        <Check className="w-3 h-3 opacity-50 flex-shrink-0" style={{ color: plan.titleColor }} />
                        <span className="font-sans text-xs opacity-60">{b}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Founding Member callout */}
                <div className="flex items-start gap-4 px-5 py-4 rounded-2xl border border-foreground/10 bg-foreground/[0.025]">
                  <Shield className="w-4 h-4 mt-0.5 opacity-50 shrink-0" />
                  <div>
                    <p className="font-grotesk text-[10px] uppercase tracking-widest mb-1">Founding Member</p>
                    <p className="font-sans text-xs opacity-50 leading-relaxed">
                      You are among Furbaby's first 1,000 members. Founding Member status is permanent.
                    </p>
                  </div>
                </div>

                {/* Your details */}
                <div className="flex flex-col gap-6">
                  <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-40">Your details</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="First name *" type="text" value={form.firstName} onChange={set("firstName")} required placeholder="Jane" />
                    <Field label="Last name *" type="text" value={form.lastName} onChange={set("lastName")} required placeholder="Tan" />
                  </div>
                  <Field label="Email *" type="email" value={form.email} onChange={set("email")} required placeholder="jane@email.com" />
                  <Field label="Phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+65" />
                </div>

                {/* Pet details */}
                <div className="flex flex-col gap-6">
                  <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-40">Your furchild</p>
                  <Field label="Pet name *" type="text" value={form.petName} onChange={set("petName")} required placeholder="Biscuit" />
                  <SpeciesSelect
                    required
                    value={form.petType}
                    onChange={v => setForm(f => ({ ...f, petType: v }))}
                  />
                  <Field label="Age" type="text" value={form.petAge} onChange={set("petAge")} placeholder="e.g. 3 years" />
                </div>

                {/* Payment */}
                <div className="flex flex-col gap-6">
                  <div className="flex items-center justify-between">
                    <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-40">Payment</p>
                    <div className="flex items-center gap-1 opacity-35">
                      <Lock className="w-3 h-3" />
                      <span className="font-grotesk text-[9px] uppercase tracking-widest">Secured</span>
                    </div>
                  </div>
                  <Field label="Name on card *" type="text" value={form.cardName} onChange={set("cardName")} required placeholder="Jane Tan" />
                  <div className="flex flex-col gap-2">
                    <label className="font-grotesk text-[10px] uppercase tracking-widest opacity-50">Card number *</label>
                    <input
                      type="text" required value={form.cardNumber}
                      onChange={set("cardNumber")}
                      placeholder="•••• •••• •••• ••••"
                      maxLength={19}
                      className="bg-transparent border-b border-foreground/20 py-3 font-sans text-sm focus:outline-none focus:border-primary transition-colors duration-300 placeholder:opacity-25 tracking-widest"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Expiry *" type="text" value={form.cardExpiry} onChange={set("cardExpiry")} required placeholder="MM / YY" maxLength={7} />
                    <Field label="CVC *" type="text" value={form.cardCvc} onChange={set("cardCvc")} required placeholder="•••" maxLength={4} />
                  </div>
                </div>

                {/* T&C checkbox */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div
                    onClick={() => setAgreeTC(v => !v)}
                    className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors duration-300 ${
                      agreeTC ? "bg-primary border-primary" : "border-foreground/30 group-hover:border-foreground/60"
                    }`}
                  >
                    {agreeTC && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                  </div>
                  <span className="font-sans text-xs opacity-55 leading-relaxed">
                    I have read and agree to the{" "}
                    <button
                      type="button"
                      onClick={e => { e.preventDefault(); document.querySelector('[data-tc]')?.scrollIntoView({ behavior: 'smooth' }); }}
                      className="underline underline-offset-2 hover:opacity-80"
                    >
                      membership terms
                    </button>
                    {" "}and authorise Furbaby Vet Care to charge my card {plan.price} annually.
                  </span>
                </label>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!agreeTC}
                  className={`group w-full flex items-center justify-between font-grotesk text-sm uppercase tracking-widest px-8 py-5 rounded-full transition-all duration-500 ${
                    agreeTC
                      ? "bg-foreground text-background hover:opacity-75"
                      : "bg-foreground/20 text-foreground/40 cursor-not-allowed"
                  }`}
                >
                  <span>Confirm &amp; Join</span>
                  <ArrowRight className={`w-4 h-4 transition-transform duration-400 ${agreeTC ? "group-hover:translate-x-1" : ""}`} />
                </button>

                <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-30 text-center">
                  Payment processing coming soon — no charge will be made today
                </p>

              </form>
            </div>
          </div>
        </div>

        {/* Invisible anchor for T&C scroll */}
        <div data-tc className="mt-0" />
      </div>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-20 border-t border-foreground/10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <span className="font-serif text-xl font-bold italic tracking-tighter">Furbaby</span>
          <span className="font-grotesk text-xs uppercase tracking-widest opacity-30">© 2024 Furbaby Vet Care · Singapore</span>
          <div className="flex gap-6 font-grotesk text-xs uppercase tracking-widest opacity-40">
            <Link href="/membership" className="hover:opacity-70 transition-opacity">Membership</Link>
            <Link href="/book" className="hover:opacity-70 transition-opacity">Book Visit</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Field({
  label, type, value, onChange, required, placeholder, maxLength,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-grotesk text-[10px] uppercase tracking-widest opacity-50">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={maxLength}
        className="bg-transparent border-b border-foreground/20 py-3 font-sans text-sm focus:outline-none focus:border-primary transition-colors duration-300 placeholder:opacity-25"
      />
    </div>
  );
}
